import React, { useCallback, useEffect, useState } from 'react';
import ConfirmationModal from '../ConfirmationModal';
import SupportActivityModal from './SupportActivityModal';
import { useAppState } from '../../context/AppStateContext';
import {
  SupportAccessService, SupportGrant, GrantState, apiErrorMessage, formatSupportExpiry, childCode,
} from '../../services/supportDataService';

/**
 * Organisation settings → Support access (tdp-api docs/support-access-plan.md §5).
 *
 * Everything here takes effect immediately, so it lives outside the settings
 * form and its Save button. Decisions are refused by the API unless the Admin
 * is signed in with their own account — the message it returns says so.
 */

const STATE_LABEL: Record<GrantState, string> = {
  pending: 'Waiting for a decision',
  lapsed: 'Lapsed (no decision within 7 days)',
  active: 'Active',
  expired: 'Expired',
  declined: 'Declined',
  revoked: 'Revoked',
};

/** What the admin is agreeing to. Repeated wherever access is granted. */
const WhatSupportSees: React.FC<{ diagnostics?: boolean }> = ({ diagnostics }) => (
  <ul className="support-access__sees">
    <li><i className="fas fa-check" aria-hidden="true" /> Support <strong>will</strong> see staff names and email addresses, settings, schedules and attendance.</li>
    {diagnostics && (
      <li><i className="fas fa-check" aria-hidden="true" /> Support <strong>will</strong> see the status of posts and reports: dates, whether images processed, and error codes.</li>
    )}
    <li><i className="fas fa-xmark" aria-hidden="true" /> Support will <strong>not</strong> see child names, or the content of posts, reports or documents.</li>
  </ul>
);

const DIAGNOSTICS_LABEL = 'Also allow support to see the status of posts and reports (never their content)';

/** The post & report status opt-in (plan §9a). Off unless ticked. */
const DiagnosticsChoice: React.FC<{ id: string; checked: boolean; disabled: boolean; onChange: (v: boolean) => void }> = (
  { id, checked, disabled, onChange },
) => (
  <div className="form-check support-access__diagnostics">
    <input id={id} type="checkbox" className="form-check-input" checked={checked} disabled={disabled} onChange={e => onChange(e.target.checked)} />
    <label className="form-check-label" htmlFor={id}>{DIAGNOSTICS_LABEL}</label>
  </div>
);

const OFFER_DAY_OPTIONS = [1, 3, 7, 14];

/** Grants under which support may have done something worth reviewing. */
const HAS_ACTIVITY: GrantState[] = ['active', 'expired', 'revoked'];

interface SupportAccessPanelProps {
  /** Called once the first load has finished and the panel is at full height
   *  (so a deep link can scroll to it without stopping short). */
  onLoaded?: () => void;
}

const SupportAccessPanel: React.FC<SupportAccessPanelProps> = ({ onLoaded }) => {
  const { childProfiles } = useAppState();
  const [activityFor, setActivityFor] = useState<number | null>(null);
  const [grants, setGrants] = useState<SupportGrant[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [approveDays, setApproveDays] = useState<number>(1);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [offerReason, setOfferReason] = useState('');
  const [offerDays, setOfferDays] = useState(3);
  const [approveDiagnostics, setApproveDiagnostics] = useState(false);
  const [offerDiagnostics, setOfferDiagnostics] = useState(false);

  const load = useCallback(async () => {
    try {
      const { grants: list } = await SupportAccessService.fetchGrants();
      setGrants(list);
      const pending = list.find(g => g.state === 'pending');
      if (pending) setApproveDays(pending.requestedDays);
      setLoadError(null);
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Could not load support access.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load().then(() => onLoaded?.());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const run = async (action: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setActionError(null);
    try {
      await action();
      await load();
    } catch (err) {
      setActionError(apiErrorMessage(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p className="text-muted mb-0">Loading support access…</p>;
  if (loadError) return <div className="alert alert-warning mb-0">{loadError}</div>;

  const pending = grants.find(g => g.state === 'pending');
  const active = grants.find(g => g.state === 'active');
  const history = grants.filter(g => g !== pending && g !== active);

  return (
    <div className="support-access">
      {actionError && <div className="alert alert-danger" role="alert">{actionError}</div>}

      {pending && (
        <div className="support-access__card support-access__card--pending">
          <div className="support-access__card-title">
            <i className="fas fa-bell" aria-hidden="true" /> Rustle It Support is asking for access
          </div>
          <dl className="support-access__facts">
            <dt>Requested by</dt><dd>{pending.requestedByName ?? 'Rustle It Support'} · {formatSupportExpiry(pending.requestedAt)}</dd>
            <dt>Reason</dt><dd>{pending.reason}</dd>
            {pending.ticketRef && (<><dt>Reference</dt><dd>{pending.ticketRef}</dd></>)}
          </dl>
          <WhatSupportSees diagnostics={approveDiagnostics} />
          <DiagnosticsChoice id="support-approve-diagnostics" checked={approveDiagnostics} disabled={busy} onChange={setApproveDiagnostics} />
          <div className="support-access__actions">
            <label className="support-access__days">
              Allow for
              <select
                className="form-select form-select-sm"
                value={approveDays}
                onChange={e => setApproveDays(Number(e.target.value))}
                disabled={busy}
              >
                {Array.from({ length: pending.requestedDays }, (_, i) => i + 1).map(d => (
                  <option key={d} value={d}>{d} day{d === 1 ? '' : 's'}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy}
              onClick={() => run(async () => {
                await SupportAccessService.approve(pending.id, approveDays, approveDiagnostics);
                setApproveDiagnostics(false);
              }, 'Could not approve access.')}
            >
              Approve
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-sm"
              disabled={busy}
              onClick={() => run(() => SupportAccessService.decline(pending.id), 'Could not decline the request.')}
            >
              Decline
            </button>
          </div>
        </div>
      )}

      {active && (
        <div className="support-access__card support-access__card--active">
          <div className="support-access__card-title">
            <i className="fas fa-life-ring" aria-hidden="true" /> Support has access until {formatSupportExpiry(active.expiresAt ?? '')}
          </div>
          <dl className="support-access__facts">
            <dt>{active.offered ? 'Given by' : 'Approved by'}</dt><dd>{active.decidedByName ?? '—'}</dd>
            <dt>Reason</dt><dd>{active.reason}</dd>
          </dl>
          <WhatSupportSees diagnostics={active.allowDiagnostics} />
          <div className="form-check form-switch support-access__diagnostics">
            <input
              id="support-active-diagnostics"
              type="checkbox"
              role="switch"
              className="form-check-input"
              checked={active.allowDiagnostics}
              disabled={busy}
              onChange={e => {
                const enabled = e.target.checked;
                void run(() => SupportAccessService.setDiagnostics(active.id, enabled),
                  enabled ? 'Could not turn on post and report status.' : 'Could not turn off post and report status.');
              }}
            />
            <label className="form-check-label" htmlFor="support-active-diagnostics">
              Let support see the status of posts and reports (never their content)
            </label>
            <div className="form-text">Takes effect straight away. Turning it off stops support seeing it on their next click.</div>
          </div>
          {childProfiles.length > 0 && (
            <details className="support-access__codes">
              <summary>Child codes support sees</summary>
              <p>Support sees each child as a code. Use this list if support mentions one.</p>
              <ul>
                {[...childProfiles].sort((a, b) => a.id - b.id).map(c => (
                  <li key={c.id}><code>{childCode(c.id)}</code> {[c.first_name, c.last_name].filter(Boolean).join(' ')}</li>
                ))}
              </ul>
            </details>
          )}
          <div className="support-access__actions">
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setActivityFor(active.id)}>
              View activity
            </button>
            <button type="button" className="btn btn-outline-danger btn-sm" disabled={busy} onClick={() => setConfirmRevoke(true)}>
              Revoke access now
            </button>
          </div>
        </div>
      )}

      {!pending && !active && (
        <div className="support-access__card">
          <div className="support-access__card-title">
            <i className="fas fa-hand-holding-heart" aria-hidden="true" /> Get help from Rustle It
          </div>
          <p className="support-access__lead">
            Give Rustle It Support temporary access to help with settings, schedules, attendance or
            report templates. Access ends automatically, and you can revoke it at any time.
          </p>
          <label className="form-label" htmlFor="support-offer-reason">What do you need help with?</label>
          <textarea
            id="support-offer-reason"
            className="form-control mb-2"
            rows={2}
            maxLength={500}
            value={offerReason}
            onChange={e => setOfferReason(e.target.value)}
            disabled={busy}
          />
          <WhatSupportSees diagnostics={offerDiagnostics} />
          <DiagnosticsChoice id="support-offer-diagnostics" checked={offerDiagnostics} disabled={busy} onChange={setOfferDiagnostics} />
          <div className="support-access__actions">
            <label className="support-access__days">
              Allow for
              <select className="form-select form-select-sm" value={offerDays} onChange={e => setOfferDays(Number(e.target.value))} disabled={busy}>
                {OFFER_DAY_OPTIONS.map(d => <option key={d} value={d}>{d} day{d === 1 ? '' : 's'}</option>)}
              </select>
            </label>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy || offerReason.trim().length < 10}
              title={offerReason.trim().length < 10 ? 'Describe what you need help with (at least 10 characters)' : undefined}
              onClick={() => run(async () => {
                await SupportAccessService.offer(offerReason.trim(), offerDays, offerDiagnostics);
                setOfferReason('');
                setOfferDiagnostics(false);
              }, 'Could not give support access.')}
            >
              Give support access
            </button>
          </div>
        </div>
      )}

      {history.length > 0 && (
        <div className="support-access__history">
          <div className="support-access__history-title">Previous access</div>
          <ul>
            {history.map(g => (
              <li key={g.id}>
                <span className={`support-access__state support-access__state--${g.state}`}>{STATE_LABEL[g.state]}</span>
                <span className="support-access__history-when">
                  {g.offered ? 'Given' : 'Requested'} {formatSupportExpiry(g.requestedAt)}
                </span>
                {HAS_ACTIVITY.includes(g.state) && (
                  <button type="button" className="btn btn-link btn-sm p-0 support-access__history-link" onClick={() => setActivityFor(g.id)}>
                    View activity
                  </button>
                )}
                <span className="support-access__history-reason">{g.reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <SupportActivityModal grantId={activityFor} onClose={() => setActivityFor(null)} />

      <ConfirmationModal
        show={confirmRevoke}
        title="Revoke support access?"
        message="Rustle It Support will lose access to your organisation straight away."
        confirmText="Revoke access"
        isDeleting={busy}
        onCancel={() => setConfirmRevoke(false)}
        onConfirm={() => {
          setConfirmRevoke(false);
          if (active) void run(() => SupportAccessService.revoke(active.id), 'Could not revoke access.');
        }}
      />
    </div>
  );
};

export default SupportAccessPanel;
